/**
 * Two-Panel Order Management Layout for RMC Orders
 * Left Panel: Order List | Right Panel: Order Summary/Editor
 */

import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import {ArrowBack, Edit} from '@mui/icons-material'
import {Box, Typography, IconButton, Button} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {useMediaQueryBreakpoints} from '../../../../../common/useMediaQueryBreakpoints'
import {useOrderEditSlotValidation} from '../../../shared/order-editor/hooks/useOrderEditSlotValidation'
import {RmcOrderEdit} from '../../../shared/order-editor/RmcOrderEdit'
import {useJobSite} from '../../contexts/JobSiteContext'
import {useRmcBulkOrderEdit} from '../../hooks/useRmcBulkOrderEdit'
import {useRmcPersistedOrderInitialization} from '../../hooks/useRmcPersistedOrderInitialization'
import {RmcOrderPayload} from '../../services/rmcOrderPlacementService'
import {RmcOrder, RmcOrderPlacementResult, RmcOrderStatus} from '../../types/order'
import {formatJobSiteInfo} from '../../utils/jobSiteUtils'
import {RmcOrderConfirmation} from '../confirmation/RmcOrderConfirmation'

import {ChangeJobSiteDiscardModal} from './ChangeJobsiteDiscardModal'
import {RmcOrderList} from './RmcOrderList'
import {getAddItemButtonStyles} from './RmcOrderList.styles'
import {RmcOrderSummary} from './RmcOrderSummary'

/**
 * Props for the RMC Order Management container
 */
interface RmcBulkOrderEditProps {
  // Initial order from completed stepper (if any)
  initialOrderData?: FieldsForm
  // Persisted order data for repeat order flow
  persistedOrders?: RmcOrderPayload[]
  onBackToStepper?: () => void
}

/**
 * Renders the confirmation screen after orders have been placed
 */
const renderConfirmationScreen = (
  orders: RmcOrder[],
  errors: Record<string, string>,
  placementResult?: RmcOrderPlacementResult | null
) => {
  const placedOrders = orders.filter((order) => order.status === RmcOrderStatus.PLACED)
  const failedOrders = orders.filter((order) => order.status === RmcOrderStatus.FAILED)

  // Use actual placement result from the hook if available, otherwise create fallback
  const finalPlacementResult: RmcOrderPlacementResult = placementResult || {
    success: placedOrders.length > 0,
    placedOrderIds: placedOrders.map((order) => order.id),
    failedOrderIds: failedOrders.map((order) => order.id),
    errors,
    requestIds: {} // Fallback empty requestIds if no placement result available
  }

  return (
    <RmcOrderConfirmation
      placementResult={finalPlacementResult}
      placedOrders={placedOrders}
      failedOrders={failedOrders}
    />
  )
}

/**
 * Two-panel layout for managing RMC orders
 * On mobile: Shows either the order list or the order edit view, with navigation between them
 */
export const RmcBulkOrderEdit: React.FC<RmcBulkOrderEditProps> = ({
  initialOrderData,
  persistedOrders,
  onBackToStepper
}) => {
  const {t} = useTranslation()
  const history = useHistory()
  const bulkOrderEdit = useRmcBulkOrderEdit()
  const {jobSite} = useJobSite()
  const [showConfirmation, setShowConfirmation] = React.useState(false)
  const [showChangeJobSiteModal, setShowChangeJobSiteModal] = React.useState(false)
  const {sm: isMobile} = useMediaQueryBreakpoints()

  const initialOrderProcessed = React.useRef(false)

  // Get slot validation for initial order dispatch approval check
  const {orderEditSlotValidation} = useOrderEditSlotValidation({jobSite})

  // Use the persistence hook to handle persisted orders
  useRmcPersistedOrderInitialization({
    persistedOrders: persistedOrders?.length ? persistedOrders : undefined,
    bulkOrderEdit
  })

  // Create initial order if provided (only once)
  React.useEffect(() => {
    if (initialOrderData && bulkOrderEdit.orders.length === 0 && !initialOrderProcessed.current) {
      bulkOrderEdit.createOrder(initialOrderData, {
        requiresDispatchApproval: orderEditSlotValidation.requiresDispatchApproval
      })
      initialOrderProcessed.current = true
    }
    // Reset flag when initialOrderData changes
    if (!initialOrderData) {
      initialOrderProcessed.current = false
    }
    // Only depend on specific values, not the entire bulkOrderEdit object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialOrderData,
    bulkOrderEdit.createOrder,
    bulkOrderEdit.orders.length,
    orderEditSlotValidation.requiresDispatchApproval
  ])

  // Sync requiresDispatchApproval status when slot validation changes for the selected order
  React.useEffect(() => {
    const selectedOrder = bulkOrderEdit.selectedOrder
    if (
      selectedOrder &&
      !orderEditSlotValidation.isLoading &&
      selectedOrder.requiresDispatchApproval !== orderEditSlotValidation.requiresDispatchApproval
    ) {
      bulkOrderEdit.updateRequiresDispatchApproval(
        selectedOrder.id,
        orderEditSlotValidation.requiresDispatchApproval
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderEditSlotValidation.requiresDispatchApproval,
    orderEditSlotValidation.isLoading,
    bulkOrderEdit.selectedOrderId
  ])

  // Check if we should show confirmation (when orders have been placed)
  React.useEffect(() => {
    const hasPlacedOrders = bulkOrderEdit.orders.some(
      (order) => order.status === RmcOrderStatus.PLACED || order.status === RmcOrderStatus.FAILED
    )

    if (hasPlacedOrders && !bulkOrderEdit.isPlacingOrders) {
      setShowConfirmation(true)
    }
  }, [bulkOrderEdit.orders, bulkOrderEdit.isPlacingOrders])

  // Handlers for change JobSite flow
  const handleStartJobSiteChange = React.useCallback(() => {
    // Check if there are orders that haven't been placed
    const shouldShowConfirmation = !!bulkOrderEdit.orders.find(
      (order) => order.status !== RmcOrderStatus.PLACED && order.status !== RmcOrderStatus.FAILED
    )

    shouldShowConfirmation
      ? setShowChangeJobSiteModal(true)
      : history.push('/overview?openJobsiteModal=true')
  }, [bulkOrderEdit.orders, history])

  const handleJobSiteChangeCancel = React.useCallback(() => {
    setShowChangeJobSiteModal(false)
  }, [])

  const handleJobSiteChangeConfirm = React.useCallback(() => {
    setShowChangeJobSiteModal(false)
    // Reset all order data before navigating away
    bulkOrderEdit.resetOrders()
    // Navigate to overview with parameter to auto-open jobsite modal
    history.push('/overview?openJobsiteModal=true')
  }, [history, bulkOrderEdit])

  // If showing confirmation, render that instead of the normal layout
  if (showConfirmation) {
    return renderConfirmationScreen(
      bulkOrderEdit.orders,
      bulkOrderEdit.errors,
      bulkOrderEdit.placementResult
    )
  }

  /**
   * Renders the mobile edit view
   */
  const renderMobileEditView = (selectedOrder: RmcOrder) => {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px'
        }}
      >
        {/* Mobile back button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <IconButton
            onClick={() => bulkOrderEdit.selectOrder(null)}
            sx={{padding: '4px'}}
            data-test-id="back-to-orders-button"
          >
            <ArrowBack sx={{fontSize: '20px', color: '#ffffff'}} />
          </IconButton>
          <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#ffffff'}}>
            {t('orderIntake.rmc.bulkOrderEdit.backToOrders')}
          </Typography>
        </Box>

        {/* Order Edit View */}
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <RmcOrderEdit
            selectedOrder={selectedOrder}
            onClose={() => bulkOrderEdit.selectOrder(null)}
            onOrderUpdate={bulkOrderEdit.updateOrder}
            jobSite={jobSite}
          />
        </Box>
      </Box>
    )
  }

  // For mobile view, when an order is selected, show only the edit view
  if (isMobile && bulkOrderEdit.selectedOrder) {
    return renderMobileEditView(bulkOrderEdit.selectedOrder)
  }

  /**
   * Renders the standard two-panel layout
   */
  const renderTwoPanelLayout = () => {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '32px',
          padding: isMobile ? '16px' : '24px'
        }}
      >
        {/* Left Panel - Order Management Table */}
        <Box
          sx={{
            flex: isMobile ? '1' : '2',
            maxWidth: isMobile ? '100%' : 'calc(66.667% - 16px)',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: isMobile ? '16px' : '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '16px' : '24px',
            height: 'fit-content'
          }}
        >
          {/* Destination Header */}
          <Box sx={{marginBottom: isMobile ? '12px' : '16px'}}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  color: '#52667a',
                  fontFamily: 'Inter'
                }}
              >
                {t('orderIntake.rmc.orderManagement.destinationLabel')}
              </Typography>

              {/* Change Jobsite Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={handleStartJobSiteChange}
                sx={{
                  ...getAddItemButtonStyles(),
                  fontSize: '12px',
                  fontWeight: 500,
                  minHeight: '32px',
                  padding: '6px 12px'
                }}
                data-test-id="change-jobsite-button"
              >
                {t('orderIntake.rmc.orderManagement.changeJobsite', 'Change jobsite')}
              </Button>
            </Box>
            <Typography
              sx={{
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: 600,
                lineHeight: isMobile ? '22px' : '24px',
                color: '#29333d',
                fontFamily: 'Inter'
              }}
            >
              {formatJobSiteInfo(jobSite)}
            </Typography>
          </Box>

          <RmcOrderList
            orders={bulkOrderEdit.orders}
            selectedOrderId={bulkOrderEdit.selectedOrderId}
            onSelectOrder={bulkOrderEdit.selectOrder}
            onDeleteOrder={bulkOrderEdit.deleteOrder}
            onAddItem={bulkOrderEdit.copyLastOrder}
            onBackToStepper={onBackToStepper}
            // Add props for mobile place order button
            isPlacingOrders={bulkOrderEdit.isPlacingOrders}
            canPlaceOrders={bulkOrderEdit.canPlaceOrders}
            onPlaceOrders={
              isMobile
                ? async () => {
                    await bulkOrderEdit.placeOrders()
                  }
                : undefined
            }
          />
        </Box>

        {/* Right Panel - Order Summary (not shown on mobile) */}
        {!isMobile && (
          <Box
            sx={{
              flex: '1',
              maxWidth: 'calc(33.333% - 16px)',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              height: 'fit-content'
            }}
          >
            {/* DEBUG 
            <pre style={{background: '#000000'}}>{JSON.stringify(formData, null, 4)}</pre>*/}
            <RmcOrderSummary
              selectedOrder={bulkOrderEdit.selectedOrder}
              orders={bulkOrderEdit.orders}
              isPlacingOrders={bulkOrderEdit.isPlacingOrders}
              placementError={bulkOrderEdit.placementError}
              canPlaceOrders={bulkOrderEdit.canPlaceOrders}
              hasDateResets={bulkOrderEdit.hasDateResets}
              onPlaceOrders={async () => {
                await bulkOrderEdit.placeOrders()
              }}
              onClearError={bulkOrderEdit.clearPlacementError}
              onSelectOrder={bulkOrderEdit.selectOrder}
              onOrderUpdate={bulkOrderEdit.updateOrder}
            />
          </Box>
        )}
      </Box>
    )
  }

  // Responsive layout
  return (
    <>
      {renderTwoPanelLayout()}

      {/* Change Jobsite Discard Modal */}
      <ChangeJobSiteDiscardModal
        open={showChangeJobSiteModal}
        onCancel={handleJobSiteChangeCancel}
        onConfirm={handleJobSiteChangeConfirm}
      />
    </>
  )
}
